import Cookies from 'js-cookie';

const SESSION_COOKIE = 'daisies_session_active';
const SESSION_EXPIRY_DAYS = 15;
const USER_PROFILE_KEY = 'daisies_user_profile';
const RESERVATION_KEY = 'daisies_reservation';

/**
 * Guarda la sesión completa del usuario (auth + perfil)
 * Cookie dura 15 días
 */
export const saveUserSession = (authData, profileData) => {
  // Auth tokens
  localStorage.setItem('daisies_user_id', String(authData.userId || ''));
  localStorage.setItem('daisies_auth_token', authData.authToken || '');
  localStorage.setItem('daisies_firebase_uid', authData.firebaseUid || '');

  // Perfil del usuario
  const profile = {
    nombre: profileData.nombre || '',
    apellido: profileData.apellido || '',
    email: profileData.email || '',
    telefono: profileData.telefono || '',
    phoneCountry: profileData.phoneCountry || 'MX',
    phoneCode: profileData.phoneCode || '+52',
    birthDate: profileData.birthDate || '',
    birthSex: profileData.birthSex || '',
    pronoun: profileData.pronoun || '',
    leadId: profileData.leadId || null
  };
  localStorage.setItem(USER_PROFILE_KEY, JSON.stringify(profile));

  // Cookie de sesión — 15 días
  Cookies.set(SESSION_COOKIE, '1', { expires: SESSION_EXPIRY_DAYS });
};

/**
 * Obtiene la sesión del usuario, o null si expiró / no existe
 */
export const getUserSession = () => {
  const cookieActive = Cookies.get(SESSION_COOKIE);
  if (!cookieActive) {
    // Cookie expiró — limpiar localStorage también
    clearSession();
    return null;
  }

  const userId = localStorage.getItem('daisies_user_id');
  const authToken = localStorage.getItem('daisies_auth_token');
  const firebaseUid = localStorage.getItem('daisies_firebase_uid');
  const profileStr = localStorage.getItem(USER_PROFILE_KEY);
  // Flag set por sessionBootstrap cuando la usuaria llega de landing ya
  // habiendo verificado WhatsApp. Optimización para saltar el gate y no
  // pedir verificación 2 veces (el sync Odoo→Firestore tiene delay).
  const phoneVerified = localStorage.getItem('daisies_phone_verified') === '1';

  if (!userId || !authToken) return null;

  let profile = {};
  try {
    profile = profileStr ? JSON.parse(profileStr) : {};
  } catch { /* ignore */ }

  return {
    userId,
    authToken,
    firebaseUid,
    phoneVerified,
    ...profile,
  };
};

/**
 * Guarda los datos de la reservación completada
 */
export const saveReservation = (reservationData) => {
  const data = {
    specialtyId: reservationData.specialtyId || null,
    specialtyName: reservationData.specialtyName || '',
    specialtyIcon: reservationData.specialtyIcon || '',
    date: reservationData.date || '',
    time: reservationData.time || '',
    doctorName: reservationData.doctorName || '',
    savedAt: new Date().toISOString()
  };
  localStorage.setItem(RESERVATION_KEY, JSON.stringify(data));
};

/**
 * Obtiene la reservación guardada, o null
 */
export const getReservation = () => {
  if (!Cookies.get(SESSION_COOKIE)) return null;
  const str = localStorage.getItem(RESERVATION_KEY);
  if (!str) return null;
  try {
    return JSON.parse(str);
  } catch {
    return null;
  }
};

/**
 * Limpia toda la sesión (logout)
 */
export const clearSession = () => {
  Cookies.remove(SESSION_COOKIE);
  localStorage.removeItem('daisies_user_id');
  localStorage.removeItem('daisies_auth_token');
  localStorage.removeItem('daisies_firebase_uid');
  localStorage.removeItem(USER_PROFILE_KEY);
  localStorage.removeItem(RESERVATION_KEY);
};

/**
 * Verifica si hay sesión activa
 */
export const isLoggedIn = () => {
  return !!Cookies.get(SESSION_COOKIE) && !!localStorage.getItem('daisies_user_id');
};

/**
 * Formatea fecha y hora para mostrar
 */
export const formatDateTime = (dateTimeStr) => {
  if (!dateTimeStr) return '';
  try {
    const date = new Date(dateTimeStr);
    return date.toLocaleDateString('es-MX', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }) + ' - ' + date.toLocaleTimeString('es-MX', {
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (e) {
    return dateTimeStr;
  }
};

/**
 * Obtiene el precio base según especialidad.
 * Fallback de última instancia cuando no se puede resolver el precio por
 * duración desde el backend (price_extra_consultations).
 */
export const getSpecialtyPrice = () => {
  return 450;
};

/**
 * Minutos de una duración (por su id) buscando en durations + extra_durations.
 * El backend manda `duration` en minutos (int(round(self.duration*60)) en
 * dais_specialty.py). Devuelve null si no se encuentra.
 */
export const getDurationMinutes = (specialty, durationId) => {
  if (durationId == null) return null;
  const all = [
    ...(specialty?.durations || []),
    ...(specialty?.extra_durations || []),
  ];
  const d = all.find((x) => Number(x?.id) === Number(durationId));
  if (!d) return null;
  const mins = d.duration ?? d.minutes;
  return Number.isFinite(Number(mins)) ? Number(mins) : null;
};

/**
 * Precio de una consulta extra para una duración dada (en MINUTOS).
 * El backend sincroniza `price_extra_consultations` keyado por `id` = minutos
 * (ver dais_specialty.py:1086-1107: "el id aquí es la duración EN MINUTOS").
 * Devuelve el precio según membresía (membership_cost) o sin ella (extra_cost),
 * o null si no hay match válido.
 */
export const getExtraConsultationPrice = (specialty, durationMinutes, hasMembership = false) => {
  if (durationMinutes == null) return null;
  const list = specialty?.price_extra_consultations || specialty?.firebaseData?.price_extra_consultations || [];
  const match = list.find((p) => Number(p?.id) === Number(durationMinutes));
  if (!match) return null;
  const raw = hasMembership
    ? (match.membership_cost ?? match.membershipCost)
    : (match.extra_cost ?? match.extraCost);
  const cost = Number(raw);
  return Number.isFinite(cost) && cost > 0 ? cost : null;
};

/**
 * Configuración de planes de membresía
 */
export const getMembershipPlans = (basePrice) => {
  return {
    monthly: {
      id: 'membership_monthly',
      odooId: 21,
      name: 'Membresía Mensual',
      price: 399,
      originalPrice: basePrice,
      savings: basePrice - 399,
      sessions: 1,
      features: [
        '70 minutos de consultas para distribuir entre las especialidades incluidas',
        'Compra más minutos a precio especial',
        '5 preguntas de orientación médica',
        'Recetas y planes personalizados',
        'Acceso a todas las funciones de la app',
        'Renovación automática, cancela cuando desees',
        'Acceso a contenido exclusivo'
      ],
      popular: true
    },
    quarterly: {
      id: 'membership_quarterly',
      odooId: 23,
      name: 'Membresía Trimestral',
      price: 1159,
      originalPrice: basePrice * 3,
      savings: (basePrice * 3) - 1159,
      sessions: 3,
      features: [
        '70 minutos de consultas para distribuir entre las especialidades incluidas',
        'Compra más minutos a precio especial',
        '5 preguntas de orientación médica',
        'Recetas y planes personalizados',
        'Acceso a todas las funciones de la app',
        'Renovación automática, cancela cuando desees',
        'Acceso a contenido exclusivo'
      ],
      popular: false
    },
    annual: {
      id: 'membership_annual',
      odooId: 25,
      name: 'Membresía Anual',
      price: 3999,
      originalPrice: basePrice * 12,
      savings: (basePrice * 12) - 3999,
      sessions: 12,
      features: [
        '70 minutos de consultas para distribuir entre las especialidades incluidas',
        'Compra más minutos a precio especial',
        '5 preguntas de orientación médica',
        'Recetas y planes personalizados',
        'Acceso a todas las funciones de la app',
        'Renovación automática, cancela cuando desees',
        'Acceso a contenido exclusivo',
        'Mejor precio por mes ($334/mes)'
      ],
      popular: false
    }
  };
};

/**
 * Formatea número de tarjeta
 */
export const formatCardNumber = (value) => {
  const formatted = value.replace(/\D/g, '').replace(/(.{4})/g, '$1 ').trim();
  return formatted.length > 19 ? formatted.substring(0, 19) : formatted;
};

/**
 * Formatea fecha de expiración
 */
export const formatExpiry = (value) => {
  return value.replace(/\D/g, '').replace(/(.{2})/g, '$1/').slice(0, 5);
};

/**
 * Formatea CVV
 */
export const formatCVV = (value) => {
  return value.replace(/\D/g, '').substring(0, 3);
};

/**
 * Valida datos completos de tarjeta
 */
export const validateCardData = (cardData) => {
  if (!cardData.number || !cardData.name || !cardData.expiry || !cardData.cvv) {
    return {
      isValid: false,
      message: 'Completa todos los campos de la tarjeta'
    };
  }
  
  // Validar longitud mínima del número de tarjeta (sin espacios)
  const cardNumberClean = cardData.number.replace(/\s/g, '');
  if (cardNumberClean.length < 15) {
    return {
      isValid: false,
      message: 'Número de tarjeta inválido'
    };
  }
  
  // Validar formato de expiración
  if (cardData.expiry.length < 5) {
    return {
      isValid: false,
      message: 'Fecha de expiración inválida'
    };
  }
  
  // Validar CVV
  if (cardData.cvv.length < 3) {
    return {
      isValid: false,
      message: 'CVV inválido'
    };
  }
  
  return { isValid: true };
};

/**
 * Extrae precio del response de la consulta
 */
export const extractPriceFromConsultation = (consultationData, basePrice = 450) => {
  return consultationData?.result?.data?.price || 
         consultationData?.result?.data?.consultation?.amount_consultation || 
         basePrice;
};

/**
 * Obtiene tokens de autenticación del localStorage
 */
export const getAuthTokens = () => {
  const authToken = localStorage.getItem('daisies_auth_token') || '';
  const userId = localStorage.getItem('daisies_user_id') || '';
  const firebaseUid = localStorage.getItem('daisies_firebase_uid') || '';
  
  return { authToken, userId, firebaseUid };
};

/**
 * Guarda tokens de autenticación en localStorage
 */
export const saveAuthTokens = (userId, authToken, firebaseUid) => {
  localStorage.setItem('daisies_user_id', String(userId));
  localStorage.setItem('daisies_auth_token', authToken);
  localStorage.setItem('daisies_firebase_uid', firebaseUid);
};

/**
 * Construye URL de redirección a la app
 */
export const buildAppRedirectUrl = (userData, userAuthData) => {
  const { authToken, userId, firebaseUid } = getAuthTokens();
  
  const params = new URLSearchParams({
    leadId: userData.leadId || '',
    userId: userId || userAuthData.userId || '',
    firebaseUid: firebaseUid || userAuthData.firebaseUid || '',
    authToken: authToken || userAuthData.authToken || '',
    email: userData.email || '',
    password: userData.password || ''
  });

  return `https://app.daisies.mx/authPage?${params.toString()}`;
};
